import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import io from "socket.io-client";

import Footer from "@/components/molecules/Footer";
import Header from "@/components/molecules/Header";
import Button from "@/components/atoms/Buttons/Button";

import i18n from "@/translate/i18n";
import { convertMsToTime, formatDocumentMask } from "@/utils";
import { useTransactionData } from "@/context/transactionContext";
import { useMerchantTheme } from "@/hooks/useMerchantTheme";
import { Trans } from "react-i18next";

import { LayoutWrapper } from "@/components/molecules/Wrappers/LayoutWrapper";
import { FakeInput } from "@/components/molecules/Wrappers/FakeInput";
import { ReactComponent as InfoIcon } from "@/assets/images/info.svg";
import { ReactComponent as SofIcon } from "@/assets/images/sof.svg";
import { ReactComponent as LogoutIcon } from "@/assets/images/logout.svg";
import { ReactComponent as PaymentIcon } from "@/assets/images/payment.svg";
import { ReactComponent as CloseIcon } from "@/assets/images/close_modal.svg";
import { ReactComponent as DocumentIcon } from "@/assets/images/document.svg";

import { cancelTransaction } from "@/services/api";
import { QrCode } from "./styles";

const ONE_SECOND = 1000;
const TEN_MINUTES = 600000;
const QUANTITY_INSTRUCTIONS = 3;
const TIME_TO_ACTIVATE = 3570;

const isValidUrl = (urlString) => {
  if (!urlString) return false;
  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

function TransactionDetails() {
  const history = useHistory();
  const { id, token } = useParams();
  const { showAlreadyPaid } = useMerchantTheme();
  const { transactionData, setTransactionData } = useTransactionData();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const dataSource = searchParams.get("source");
  const kycRequired = location.state?.kycRequired;

  const isQrPresent = useMemo(
    () => Boolean(transactionData?.bank_payment_data?.qr_code),
    [transactionData],
  );

  const initialTimerState = transactionData?.seconds_to_expire
    ? transactionData.seconds_to_expire * ONE_SECOND
    : TEN_MINUTES;

  const [counter, setCounter] = useState(initialTimerState);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const validationNeeded = false;
  const [isButtonAvailable, setIsButtonAvailable] = useState(() => {
    const initialSeconds = initialTimerState / ONE_SECOND;
    return initialSeconds <= TIME_TO_ACTIVATE || validationNeeded;
  });
  const [cancelError, setCancelError] = useState(null);

  const validRedirectUrlFailed = useMemo(
    () =>
      isValidUrl(transactionData?.redirect_url_failed)
        ? transactionData.redirect_url_failed
        : null,
    [transactionData?.redirect_url_failed],
  );

  const openModal = useCallback(() => {
    setCancelError(null);
    setIsOpenModal(true);
  }, []);

  const handleCancelTransaction = useCallback(async () => {
    setCancelError(null);
    try {
      await cancelTransaction(id, token);
      const updatedTransaction = {
        ...transactionData,
        status: "cancelled",
      };
      setTransactionData(updatedTransaction);
      setIsOpenModal(false);

      history.push({
        pathname: `/transaction-status/${id}/${token}`,
        search: history.location.search,
        state: {
          data: updatedTransaction,
        },
      });
    } catch (err) {
      const message = err?.response?.data?.message;
      setCancelError(message);
    }
  }, [id, token, transactionData, setTransactionData, history]);

  useEffect(() => {
    if (!transactionData?.status) return;

    if (
      transactionData?.status === "cancelled" ||
      transactionData?.status === "expired"
    ) {
      history.push({
        pathname: `/transaction-status/${id}/${token}`,
        search: location.search,
        state: {
          data: transactionData,
        },
      });
      return;
    }

    if (transactionData?.status !== "pending") {
      history.push({
        pathname: `/deposit/status/${id}/${token}`,
        search: location.search,
        state: {
          data: transactionData,
        },
      });
    }
  }, [
    transactionData?.status,
    history,
    id,
    token,
    location.search,
    transactionData,
  ]);

  useEffect(() => {
    if (transactionData && !transactionData.id) {
      history.push(`/${id}/${token}${history.location.search}`);
    }
  }, [transactionData, id, token, history, location.search]);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_SERVER_URL, {
      path: "/websocket/socket.io",
    });

    socket.on("confirmTransaction", (socketData) => {
      if (socketData.id === Number(id)) {
        socket.disconnect();
        const mergedData = { ...transactionData, ...socketData };
        if (socketData?.kyc_required && isValidUrl(socketData.redirect_url)) {
          window.location.replace(socketData.redirect_url);
        } else if (socketData?.status !== "pending") {
          history.push({
            pathname: `/deposit/status/${id}/${token}`,
            search: history.location.search,
            state: { data: mergedData },
          });
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [id, history, token, transactionData]);

  useEffect(() => {
    if (counter <= 0) {
      const expiredData = {
        ...transactionData,
        status: "expired",
        message: "transaction_expired",
      };
      setTransactionData(expiredData);
      history.push({
        pathname: `/deposit/status/${id}/${token}`,
        search: location.search,
        state: {
          data: expiredData,
        },
      });
      return undefined;
    }

    const timer = setInterval(
      () => setCounter((prev) => prev - ONE_SECOND),
      ONE_SECOND,
    );
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counter, history, id, token, location.search, setTransactionData]);

  useEffect(() => {
    if (isButtonAvailable) {
      return;
    }
    const currentSeconds = counter / ONE_SECOND;
    if (currentSeconds <= TIME_TO_ACTIVATE) {
      setIsButtonAvailable(true);
    }
  }, [counter, isButtonAvailable]);

  const isTimerRunning = counter > 0;

  return (
    <>
      {isOpenModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
          <div className="relative bg-white py-8 px-10 rounded-lg shadow-xl flex flex-col gap-4 items-center max-w-[22rem]">
            <CloseIcon
              className="w-8 z-8 absolute right-3 top-3 cursor-pointer"
              onClick={() => setIsOpenModal(false)}
            />
            <LogoutIcon className="w-6 h-6 my-2" onClick={openModal} />
            <h2 className="text-black text-lg max-w-xs font-semibold">
              {i18n.t("leaveTransaction.title")}
            </h2>
            <p className="text-center">{i18n.t("leaveTransaction.info")}</p>
            {cancelError && (
              <p className="text-center text-red-500">{cancelError}</p>
            )}
            <Button marginTop="10px" handleClick={() => setIsOpenModal(false)}>
              {i18n.t("leaveTransaction.button")}
            </Button>
            <a
              href={validRedirectUrlFailed || "#"}
              className="font-normal text-cripten-blue underline"
              onClick={(e) => {
                e.preventDefault();
                handleCancelTransaction();
              }}
            >
              {i18n.t("leaveTransaction.link")}
            </a>
          </div>
        </div>
      )}
      <div className="flex justify-center items-center bg-white sm:bg-gray-900 font-arboria-book min-h-screen overflow-x-hidden">
        <div className="flex flex-col justify-between items-center w-[500px] rounded-[20px] overflow-hidden text-center">
          <Header
            title={isTimerRunning ? i18n.t("header.previous") : null}
            time={convertMsToTime(counter)}
          />
          <LayoutWrapper>
            <LogoutIcon
              className="w-5 z-10 absolute right-8 sm:right-10 top-4 sm:top-7 cursor-pointer"
              onClick={() => setIsOpenModal(true)}
            />
            <div className="mb-10 text-black text-lg max-w-xs font-semibold">
              {i18n.t("checkout.title")}
            </div>

            {isQrPresent ? (
              <QrCode
                value={transactionData?.bank_payment_data?.qr_code}
                alt="QR Code"
              />
            ) : (
              <>
                <FakeInput
                  label={i18n.t("form.destinataryCBU")}
                  value={String(
                    transactionData?.bank_payment_data?.account_id || "",
                  )}
                  dataCopy={transactionData?.bank_payment_data?.account_id}
                  hasAnimation
                />
                <FakeInput
                  className="mb-1"
                  label={i18n.t("form.amountLabel.deposit")}
                  value={String(transactionData?.merchant_request_amount)}
                  description={transactionData?.merchant_request_currency_iso}
                />
                <span className="text-left text-sm mb-6 mt-0">
                  {i18n.t("form.amountLabel.restriction3000")}
                </span>
                <FakeInput
                  className="mb-6"
                  label={i18n.t("form.destinataryLabel")}
                  value={String(
                    transactionData?.bank_payment_data?.account_holder || "",
                  )}
                />
              </>
            )}

            {dataSource === "merchant" && (
              <>
                {/* Instructions */}
                <div className="self-start mb-5 font-bold text-cripten-blue">
                  <button
                    className="flex items-center gap-2"
                    type="button"
                    onClick={() => setShowInstructions((state) => !state)}
                  >
                    <PaymentIcon />
                    {i18n.t("checkout.paymentInstructions")}
                  </button>
                </div>
                <div
                  className={`flex justify-end w-full overflow-hidden transition-all duration-500 ease-in-out
              ${showInstructions ? "max-h-[500px]" : "max-h-0"}
              `}
                >
                  <div className="font-medium relative mb-8 py-4 px-4 text-left text-sm w-[calc(100%_-_26px)]">
                    <ul>
                      {Array.from({ length: QUANTITY_INSTRUCTIONS }).map(
                        (_, i) => {
                          const iterationNumber = i + 1;
                          const instructionText = isQrPresent
                            ? i18n.t(
                                `checkout.instructions_qr.${iterationNumber}`,
                              )
                            : i18n.t(
                                `checkout.instructions.${iterationNumber}`,
                              );
                          return (
                            <li className="text-black" key={iterationNumber}>
                              {`${iterationNumber}. ${instructionText}`}
                            </li>
                          );
                        },
                      )}
                    </ul>
                    <div className="absolute top-0 left-0 opacity-[.15] rounded-md size-full bg-cripten-blue" />
                  </div>
                </div>
                <FakeInput
                  className="mb-6"
                  label={i18n.t("form.payerDocument")}
                  value={formatDocumentMask(
                    transactionData?.customer?.document_number,
                    transactionData?.customer?.document_type,
                  )}
                />
                <div className="flex mb-5 items-center relative gap-4 px-5 py-2 rounded-[10px] overflow-hidden w-full text-[0.8rem] text-left">
                  <InfoIcon className="w-20 z-10" />
                  <span className="leading-snug text-black">
                    {i18n.t("general.cuitCuilInstructions")}
                  </span>
                  <div className="absolute top-0 left-0 size-full bg-cripten-primary opacity-30" />
                </div>
                {transactionData?.customer?.pending_kyc_url &&
                  transactionData?.customer?.level === 1 && (
                    <button
                      type="button"
                      className="flex mb-5 items-center relative gap-5 px-5 py-2 rounded-[10px] overflow-hidden w-full text-[0.8rem] text-left cursor-pointer group"
                      onClick={() => {
                        history.push(
                          `/kyc/2/customer/${transactionData?.customer?.id}/${token}?autoStart=true`,
                        );
                      }}
                    >
                      <DocumentIcon className="w-28 z-10" />
                      <span className="leading-snug text-black z-10">
                        <Trans
                          i18nKey="general.identityInstructions"
                          components={{
                            1: (
                              <span className="text-blue-600 underline font-medium" />
                            ),
                          }}
                        />
                      </span>
                      <div className="absolute top-0 left-0 size-full bg-cripten-red opacity-20" />
                    </button>
                  )}
                {transactionData?.customer?.pending_kyc_url &&
                  transactionData?.customer?.level === 2 && (
                    <button
                      type="button"
                      className="flex mb-5 items-center relative gap-5 px-7 py-2 rounded-[10px] overflow-hidden w-full text-[0.8rem] text-left cursor-pointer group"
                      onClick={() => {
                        history.push(
                          `/kyc/3/customer/${transactionData?.customer?.id}/${token}/sof`,
                          {
                            merchant: transactionData?.merchant,
                            transactionId: id,
                            kycRequired,
                            redirect_urls: {
                              success:
                                transactionData?.redirect_url_success || null,
                              failed:
                                transactionData?.redirect_url_failed || null,
                            },
                          },
                        );
                      }}
                    >
                      <SofIcon className="w-32 z-10" />
                      <span className="leading-snug text-black z-10">
                        <Trans
                          i18nKey="general.sofInstructions"
                          components={{
                            1: (
                              <span className="text-blue-600 underline font-medium" />
                            ),
                          }}
                        />
                      </span>
                      <div className="absolute top-0 left-0 size-full bg-cripten-red opacity-20" />
                    </button>
                  )}
              </>
            )}
            {validationNeeded && (
              <div className="flex mt-5 bg-cripten-pink items-center relative gap-4 px-5 py-2 rounded-[10px] overflow-hidden w-full text-[0.8rem] text-left">
                <DocumentIcon className="w-28" />
                <span>{i18n.t("success.validationNeeded")}</span>
              </div>
            )}
            {showAlreadyPaid && (
              <Button
                type="button"
                disabled={!isButtonAvailable}
                handleClick={() => {
                  if (validationNeeded) {
                    const externalUrl =
                      "https://magic.veriff.me/v/eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpYXQiOjE3NjI5NjkwOTgsInNlc3Npb25faWQiOiI0YTZmZTczYy1kMTA2LTRmMzUtOWI4ZS1kM2ZlMGI1Mzk5MzciLCJpaWQiOiI2MDRjOGI5YS1mYzFlLTRhMmEtYjlmMy1kMmE0MzAyYzQyMjQiLCJ2aWQiOiI4ZmRiNmJkOC1kZWEyLTQ4NGQtYTk5Yi0yYzY3MjA0N2RkZDIiLCJjaWQiOiJmYWxjb24tNCIsImV4cCI6MTc2MzU3Mzg5OH0.cMKwiw04eIvTs-ut3vKQ1VqYPQ8AlZJ9io4-Q1QAEMc";
                    window.open(externalUrl, "_blank", "noopener,noreferrer");
                  } else {
                    history.push({
                      pathname: "/transaction-already-paid",
                      state: { transactionData },
                    });
                  }
                }}
              >
                {i18n.t(
                  validationNeeded
                    ? "confirmation.validateIdentityNow"
                    : "general.alreadyPay",
                )}
              </Button>
            )}
          </LayoutWrapper>
          <Footer />
        </div>
      </div>
    </>
  );
}

export default TransactionDetails;
